/**
 * Cabecera canónica de páginas de administración.
 *
 * Render:
 *   [ <h2>{title}</h2> (i) ]                [ search · filters · extras · [Exportar] · [Nuevo X] ]
 *
 * Todos los componentes Admin*.tsx deben usar este primitivo — elimina ~80% del
 * CSS duplicado detectado en el inventario M1 y centraliza el InfoTooltip.
 */

import type { ReactNode } from 'react';
import { InfoTooltip } from '@/components/ui/InfoTooltip';
import { ADMIN_TOKENS } from './adminTokens';

export interface AdminPageHeaderProps {
  title: string;
  infoTooltip?: { title?: string; text: string; width?: number };
  search?: {
    value: string;
    onChange: (v: string) => void;
    placeholder: string;
    width?: number;
  };
  filters?: ReactNode;
  extras?: ReactNode;
  actions?: {
    export?: { label: string; onClick: () => void };
    custom?: ReactNode;
  };
  onNew?: { label: string; onClick: () => void; disabled?: boolean };
}

function PlusIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <path
        d="M7 1.5V12.5M1.5 7H12.5"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}

function ExportIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <path
        d="M7 9V1.5M7 9L4 6M7 9L10 6M1.5 10.5V12H12.5V10.5"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function AdminPageHeader({
  title,
  infoTooltip,
  search,
  filters,
  extras,
  actions,
  onNew,
}: AdminPageHeaderProps) {
  return (
    <div style={ADMIN_TOKENS.headerRoot}>
      <h2 style={ADMIN_TOKENS.h2}>
        {title}
        {infoTooltip && (
          <InfoTooltip
            text={infoTooltip.text}
            title={infoTooltip.title}
            position="right"
            width={infoTooltip.width ?? 360}
          />
        )}
      </h2>

      <div style={ADMIN_TOKENS.controls}>
        {search && (
          <input
            type="text"
            value={search.value}
            onChange={(e) => search.onChange(e.target.value)}
            placeholder={search.placeholder}
            style={{
              ...ADMIN_TOKENS.search,
              width: search.width ?? ADMIN_TOKENS.search.width,
            }}
          />
        )}

        {filters}
        {extras}

        {actions?.export && (
          <button
            type="button"
            onClick={actions.export.onClick}
            style={ADMIN_TOKENS.button.secondary}
            aria-label={actions.export.label}
          >
            <ExportIcon />
            {actions.export.label}
          </button>
        )}

        {actions?.custom}

        {onNew && (
          <button
            type="button"
            onClick={onNew.onClick}
            disabled={onNew.disabled}
            style={{
              ...ADMIN_TOKENS.button.primary,
              ...(onNew.disabled ? { opacity: 0.5, cursor: 'not-allowed' } : {}),
            }}
            aria-label={onNew.label}
          >
            <PlusIcon />
            {onNew.label}
          </button>
        )}
      </div>
    </div>
  );
}
